/**
 * The Design space card: the parameters the optimizer searches, in two
 * panels the code keeps apart.
 *
 * The laser design-space parameters (frequency, speed, power, pulse
 * duration) live in the laser config and must stay within the laser's
 * hardware limits. The cluster design-space parameters (pass count,
 * hatching, line count) describe how each cluster of lines is written and
 * live in the exploration config (optimization.cluster_design_space_parameters).
 * Both go through the same editor; a parameter is optimised over its
 * values or fixed at its default.
 */
import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import LaserDesignSpaceEditor from "../../../components/LaserDesignSpaceEditor";
import { useInvalidTotal } from "./SettingFields";
import { INK } from "../../../components/cardChrome";

function count(params) {
  const entries = Object.values(params && typeof params === "object" ? params : {}).filter(
    (param) => param && typeof param === "object"
  );
  return { all: entries.length, optimised: entries.filter((param) => param.optimize).length };
}

/** "6 of 7 parameters optimised" over laser and cluster parameters. */
export function designSpaceSummary(laserConfig, explorationConfig) {
  const laser = count(laserConfig?.laser_design_space_parameters);
  const cluster = count(explorationConfig?.optimization?.cluster_design_space_parameters);
  if (laser.all === 0 && cluster.all === 0) return "";
  return `${laser.optimised + cluster.optimised} of ${laser.all + cluster.all} parameters optimised`;
}

function Panel({ title, intro, params, children }) {
  const c = count(params);
  return (
    <Stack spacing={1} sx={{ width: "100%" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" sx={{ display: "block", letterSpacing: 1.2, fontWeight: 700, lineHeight: 1.6, color: INK }}>
            {title}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#64748b" }}>
            {intro}
          </Typography>
        </Box>
        {c.all > 0 ? (
          <Chip size="small" label={`${c.optimised} of ${c.all} optimised`} sx={{ bgcolor: "#e6f0fb", color: "#1e4fa3", fontWeight: 700, flexShrink: 0 }} />
        ) : null}
      </Stack>
      {children}
    </Stack>
  );
}

export default function DesignSpaceCard({
  laserConfig,
  onLaserConfigChange,
  explorationConfig,
  onExplorationConfigChange,
  onInvalidChange,
}) {
  const setterFor = useInvalidTotal(onInvalidChange);
  const laserParams = laserConfig?.laser_design_space_parameters;
  const clusterParams = explorationConfig?.optimization?.cluster_design_space_parameters;

  if (!laserParams && !clusterParams) {
    return (
      <Typography variant="body2" color="text.secondary">
        Choose a laser and a preset to load the design space.
      </Typography>
    );
  }

  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      {laserParams ? (
        <Panel
          title="Laser parameters"
          intro="The laser design space: the values of frequency, speed, power and pulse duration the optimizer may choose from, within the laser's limits"
          params={laserParams}
        >
          <LaserDesignSpaceEditor
            params={laserParams}
            onChange={(next) => onLaserConfigChange({ ...laserConfig, laser_design_space_parameters: next })}
            onInvalidChange={setterFor("laser")}
            showCalibration
            testPrefix="design-laser"
          />
        </Panel>
      ) : null}
      {clusterParams ? (
        <Panel
          title="Cluster parameters"
          intro="The cluster design space: how each cluster of lines is written, as passes, hatching distance (mm) and number of lines"
          params={clusterParams}
        >
          <LaserDesignSpaceEditor
            params={clusterParams}
            onChange={(next) =>
              onExplorationConfigChange({
                ...explorationConfig,
                optimization: { ...explorationConfig.optimization, cluster_design_space_parameters: next },
              })
            }
            onInvalidChange={setterFor("cluster")}
            showCalibration={false}
            testPrefix="design-cluster"
          />
        </Panel>
      ) : null}
    </Stack>
  );
}
